import React, { useState, useEffect } from "react";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { FaFileImage, FaSpinner } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import uploadToCloudinary from "../../utils/cloudinary";
import axiosInstance from "../../utils/axios";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const makeId = () => uuidv4();

const UploadCertificateModal = ({ onSubmit, onClose }) => {
  useBodyScrollLock(true);

  const [formData, setFormData] = useState({ title: "", sequence: "" });
  const [image, setImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [anyImageUploading, setAnyImageUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const placeholder = {
      localId: makeId(),
      preview: URL.createObjectURL(file),
      isUploading: true,
      url: null,
    };
    setImage(placeholder);

    try {
      setAnyImageUploading(true);
      const uploadedUrl = await uploadToCloudinary(file);
      setImage({ ...placeholder, url: uploadedUrl, isUploading: false });
      setUploadedImageUrl(uploadedUrl);
    } catch (err) {
      console.error("Upload failed:", err);
      setImage(null);
    } finally {
      setAnyImageUploading(false);
    }
    e.target.value = null;
  };

  const removeImage = () => {
    if (image?.preview) URL.revokeObjectURL(image.preview);
    setImage(null);
    setUploadedImageUrl("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData, image: uploadedImageUrl };
    onSubmit(payload);
    setFormData({ title: "", sequence: "" });
    setImage(null);
    setUploadedImageUrl("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-6 relative">
        <h2 className="text-xl font-semibold mb-4">Add New Certificate</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Certificate Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded-lg outline-primaryColor"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block font-medium text-sm mb-1">
              Certificate Image{" "}
              <span className="text-xs text-amber-600">(Max size 10mb)</span>
            </label>
            <div className="flex items-start gap-4">
              {!image && (
                <label className="flex flex-col items-center justify-center w-24 h-16 border-2 border-dashed border-gray-300 rounded-lg hover:border-primaryColor transition-colors cursor-pointer">
                  <FaFileImage className="text-xl text-gray-400 mb-2" />
                  <span className="text-xs text-gray-500">Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}

              {image && (
                <div className="relative flex-shrink-0 w-24 h-16">
                  <img
                    src={image.preview || image.url}
                    alt="Upload Preview"
                    className="w-full h-full object-cover rounded border"
                  />
                  {image.isUploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center z-10">
                      <FaSpinner className="text-white animate-spin" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 pb-0.5 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                  >
                    <span>x</span>
                  </button>
                </div>
              )}
            </div>
            <p className="mt-1 text-[0.6rem] px-1 py-1 text-gray-500">
              Only one image per certificate
            </p>
          </div>

          {/* Sequence */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Sequence Number (optional)
            </label>
            <input
              type="number"
              min="1"
              name="sequence"
              value={formData.sequence}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded-lg outline-primaryColor"
            />
          </div>

          <p className="text-xs text-gray-500">
            Note: Certificates will be displayed in order of their sequence. If
            two have the same number, they may appear randomly.
          </p>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={anyImageUploading}
              className="px-4 py-2 rounded bg-primaryColor text-white hover:opacity-90 disabled:opacity-50"
            >
              {anyImageUploading ? "Uploading..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CertificateCard = ({ item, onDelete, onView }) => {
  const handleDelete = (item) => {
    Swal.fire({
      title: "ARE YOU SURE?",
      text: `Do you really want to delete "${item.title || "this image"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "YES, DELETE IT!",
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(item._id);
        Swal.fire("DELETED!", "The image has been deleted.", "success");
      }
    });
  };

  return (
    <div className="w-full group h-full flex flex-col cursor-pointer gap-y-4 my-3 sm:my-0">
      <div
        onClick={() => onView(item)} // 🔹 Added
        className="w-full h-56 lg:h-72 p-1 rounded-sm border-8 border-gray-800 shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105 relative bg-white flex items-center justify-center"
      >
        {/* CERTIFICATE IMAGE */}
        <img
          src={item?.image}
          alt="certificate"
          className="w-full h-full object-cover object-center rounded-sm"
        />

        {/* DELETE BUTTON */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(item);
          }}
          className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-md text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          Delete
        </button>

        {/* SEQUENCE BADGE */}
        {item?.sequence && (
          <span className="absolute bottom-3 left-3 bg-gray-800 text-white text-xs font-medium px-2 py-0.5 rounded-md shadow">
            Seq: {item.sequence}
          </span>
        )}
      </div>

      {/* TITLE */}
      <p className="capitalize text-sm sm:text-base text-gray-700 leading-relaxed px-4 text-center">
        {item?.title}
      </p>
    </div>
  );
};

const CertificateSkeleton = () => {
  return (
    <div className="w-full group h-full flex flex-col gap-y-4 animate-pulse">
      {/* Image placeholder */}
      <div className="w-full h-56 lg:h-72 p-1 rounded-sm border-8 border-gray-300 bg-gray-200" />

      {/* Title placeholder */}
      <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto" />

      {/* Sequence badge placeholder */}
      <div className="h-3 w-10 bg-gray-300 rounded absolute bottom-3 left-3 opacity-50" />

    </div>
  );
};

const NoCertificatesFound = ({ handleAddNewClick }) => {
  return (
    <>
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <FaFileImage className="text-6xl text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          No Certificates Found
        </h3>
        <p className="text-sm text-gray-500 mb-6 max-w-md">
          You haven’t added any certificates yet. Start by creating your first
          certificate to showcase your achievements.
        </p>
        <button
          onClick={handleAddNewClick}
          className="px-6 py-2.5 rounded-lg bg-primaryColor text-white shadow hover:opacity-90 transition"
        >
          + Create New Certificate
        </button>
      </div>
    </>
  );
};

const AdminCertificates = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewImage, setViewImage] = useState(null);

  const handleAddNewClick = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  const handleSaveCertificate = async (data) => {
    try {
      const resp = await axiosInstance.post(`/certificate`, data);
      if (resp?.data) {
        toast.success("Certificate Uploaded");
        fetchCertificates();
      }
    } catch (error) {
      console.error("Error in uploading certificate", error);
      toast.error("Failed to upload certificate");
    } finally {
      handleClose();
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get(`/certificate`);
      setCertificates(resp.data || []);
    } catch (error) {
      console.error("Error fetching certificates", error);
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const resp = await axiosInstance.delete(`/certificate/${id}`);
      if (resp?.data) {
        setCertificates((prev) => prev.filter((c) => c._id !== id));
      }
    } catch (error) {
      console.error("Error deleting certificate", error);
      toast.error("Failed to delete certificate");
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  // Filtered list by search
  const filteredCertificates = certificates.filter((c) =>
    c.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setViewImage(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <div>
      <AdminSearchBar
        title={"Certifications Management"}
        onSearchClick={(q) => setSearchQuery(q)}
        onAddNewClick={handleAddNewClick}
        hideAddButton={false}
      />

      {/* Certificate Grid */}
      <div className="p-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <CertificateSkeleton key={i} />
            ))}
          </div>
        ) : filteredCertificates.length === 0 ? (
          <NoCertificatesFound handleAddNewClick={handleAddNewClick} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCertificates.map((item) => (
              <CertificateCard
                key={item._id}
                item={item}
                onDelete={handleDelete}
                onView={(item) => setViewImage(item)}
              />
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <UploadCertificateModal
          onSubmit={handleSaveCertificate}
          onClose={handleClose}
        />
      )}

      {viewImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center justify-center">
          <button
            onClick={() => setViewImage(null)}
            className="absolute top-6 right-6 text-white text-2xl font-bold"
          >
            ×
          </button>
          <img
            src={viewImage.image}
            alt={viewImage.title}
            className="max-w-[90%] max-h-[80%] rounded shadow-lg"
          />
          <p className="text-gray-300 text-sm mt-3">Press ESC to close</p>
        </div>
      )}
    </div>
  );
};

export default AdminCertificates;
